import { yupResolver } from '@hookform/resolvers/yup';
import * as Header from '-/Custom/Header';
import {
  Link,
  Stack,
  useLocalSearchParams,
  usePathname,
  useRouter,
} from 'expo-router';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useClient, useMutation, useQuery } from 'urql';
import * as Yup from 'yup';
import TeamProfileFields from '@/components/FormFields/TeamProfileFields';
import type { TeamEditFormValues } from '@/components/Team/Team.types';
import TeamEditInvite from '@/components/Team/TeamEditInvite';
import FormContentBoundary from '@/components/UI/FormContentBoundary';
import Item from '@/components/UI/PressableRow';
import Switch from '@/components/UI/Switch';
import Text from '@/components/UI/Text';
import { graphql } from '@/gql/graphql';
import useGenerateImageUploadUrls from '@/hooks/useGenerateImageUploadUrls';
import useUploadImage from '@/hooks/useUploadImage';
import { MAXIMUM_TEAM_TITLE_LENGTH } from '@/utils/constants';
import getRandomColor from '@/utils/getRandomColor';
import FormSaveButton from '../Custom/FormSaveButton';
import ModalKeyboardAwareScrollView from '../Custom/ModalKeyboardAwareScrollView';
import ModalKeyboardStickyView from '../Custom/ModalKeyboardStickyView';
import Box from '../UI/Box';
import Card from '../UI/Card';
import Icon from '../Unistyles/Icon';

const Schema: Yup.ObjectSchema<TeamEditFormValues> = Yup.object({
  color: Yup.string().nullable(),
  title: Yup.string().max(MAXIMUM_TEAM_TITLE_LENGTH).required('Required'),
  tagGroupId: Yup.string().nullable(),
  isLocked: Yup.boolean(),
  isManager: Yup.boolean(),
  avatarId: Yup.string().nullable(),
  localAvatarUrl: Yup.string().nullable(),
});

const GetTeamUpdate = graphql(/* GraphQL */ `
  query GetTeamUpdate($id: uuid!) {
    team_by_pk(id: $id) {
      id
      title
      on_team
      home_primary_color
      avatar_id
      is_locked
      team_people_aggregate {
        aggregate {
          count
        }
      }
      root_event {
        id
        title
        event_teams(where: { team_id: { _eq: $id } }, limit: 1) {
          event_id
          team_id
          is_manager
        }
        permissions
      }
      permissions
      tag_group_id
    }
  }
`);

const UpdateTeam = graphql(/* GraphQL */ `
  mutation UpdateTeam(
    $id: uuid!
    $tagGroupId: uuid
    $title: String
    $color: String
    $avatarId: uuid
    $isLocked: Boolean
    $updateProfile: Boolean!
    $insertManagement: Boolean!
    $updateManagement: Boolean!
    $managementLink: event_team_management_insert_input!
    $managementWhere: event_team_management_bool_exp!
    $isManager: Boolean!
  ) {
    update_team_by_pk(
      pk_columns: { id: $id }
      _set: {
        title: $title
        home_primary_color: $color
        avatar_id: $avatarId
        is_locked: $isLocked
        tag_group_id: $tagGroupId
      }
    ) @include(if: $updateProfile) {
      id
      title
      home_primary_color
      avatar_id
      is_locked
      tag_group_id
    }
    insert_event_team_management_one(object: $managementLink)
      @include(if: $insertManagement)
    {
      event_id
      team_id
      is_manager
    }
    update_event_team_management(
      where: $managementWhere
      _set: { is_manager: $isManager }
    ) @include(if: $updateManagement) {
      affected_rows
    }
  }
`);

const InsertTeam = graphql(/* GraphQL */ `
  mutation InsertTeam(
    $title: String!
    $color: String
    $tagGroupId: uuid
    $avatarId: uuid
    $rootEventId: uuid
  ) {
    insert_team_one(
      object: {
        title: $title
        home_primary_color: $color
        tag_group_id: $tagGroupId
        avatar_id: $avatarId
        root_event_id: $rootEventId
      }
    ) {
      id
      title
      avatar_id
      tag_group_id
    }
  }
`);

export default function TeamEdit() {
  const client = useClient();
  const uploadImage = useUploadImage();
  const [, updateTeam] = useMutation(UpdateTeam);
  const [, insertTeam] = useMutation(InsertTeam);
  const { back, replace } = useRouter();
  const generateImageUploadUrls = useGenerateImageUploadUrls();
  const {
    teamId: id,
    rootEventId,
    rootEventTitle,
  } = useLocalSearchParams<{
    teamId?: string;
    rootEventId?: string;
    rootEventTitle?: string;
  }>();

  const form = useForm<TeamEditFormValues>({
    defaultValues: async () => {
      if (id) {
        const { data } = await client.query(
          GetTeamUpdate,
          {
            id,
          },
          { requestPolicy: 'network-only' },
        );

        return {
          avatarId: data?.team_by_pk?.avatar_id || '',
          color: data?.team_by_pk?.home_primary_color || '',
          title: data?.team_by_pk?.title || '',
          tagGroupId: data?.team_by_pk?.tag_group_id,
          isLocked: !!data?.team_by_pk?.is_locked,
          isManager:
            data?.team_by_pk?.root_event?.event_teams[0]?.is_manager ?? false,
        };
      }

      return {
        avatarId: null,
        color: getRandomColor(),
        title: '',
        tagGroupId: null,
        isLocked: false,
        isManager: false,
      };
    },
    resolver: yupResolver(Schema),
  });

  const {
    setValue,
    handleSubmit,
    control,
    formState: { isDirty, isLoading, isSubmitting, errors, dirtyFields },
  } = form;

  const [{ data }] = useQuery({
    query: GetTeamUpdate,
    variables: { id: id || '' },
    pause: !id,
    requestPolicy: 'network-only',
  });

  const team = data?.team_by_pk;
  const personCount = team?.team_people_aggregate.aggregate?.count || 0;

  const onSubmit = async (values: TeamEditFormValues) => {
    form.clearErrors('root');
    let avatarId = team?.avatar_id;

    if (values.localAvatarUrl && team?.avatar_id !== values.localAvatarUrl) {
      const { data: uploadUrls } = await generateImageUploadUrls(1);

      const uploadUrl = uploadUrls?.generateImageUploadUrls?.[0]?.uploadURL;

      const uploadPath = id
        ? `team/${id}/profile`
        : `team/create/${values.title.trim() || 'team'}/profile`;

      avatarId = await uploadImage(
        uploadPath,
        values.localAvatarUrl,
        uploadUrl || '',
      );
    }

    if (id) {
      const organization = team?.root_event;
      const eventLink = organization?.event_teams[0];
      const updateProfile = Boolean(
        team?.permissions?.edit &&
          Object.keys(dirtyFields).some((field) => field !== 'isManager'),
      );
      const changeManagement = Boolean(
        organization?.permissions?.delegate &&
          values.isManager !== (eventLink?.is_manager ?? false),
      );
      const insertManagement = changeManagement && !eventLink;
      const updateManagement = changeManagement && Boolean(eventLink);
      const { data: savedTeam, error } = await updateTeam({
        id,
        tagGroupId: values.tagGroupId,
        title: values.title,
        color: values.color,
        avatarId,
        isLocked: values.isLocked,
        updateProfile,
        insertManagement,
        updateManagement,
        managementLink: { event_id: organization?.id, team_id: id },
        managementWhere: {
          event_id: { _eq: organization?.id },
          team_id: { _eq: id },
        },
        isManager: Boolean(values.isManager),
      });
      if (
        error ||
        (updateProfile && !savedTeam?.update_team_by_pk) ||
        (insertManagement && !savedTeam?.insert_event_team_management_one) ||
        (updateManagement &&
          savedTeam?.update_event_team_management?.affected_rows !== 1)
      ) {
        form.setError('root', {
          message: 'Could not save the team. Please try again.',
        });
        return;
      }
      back();
    } else {
      const { error, data: insertTeamData } = await insertTeam({
        tagGroupId: values.tagGroupId,
        title: values.title,
        color: values.color,
        avatarId,
        rootEventId,
      });

      if (!error) {
        replace({
          pathname: '/team/create/done',
          params: {
            teamId: insertTeamData?.insert_team_one?.id,
          },
        });
      }
    }
  };

  const pathname = usePathname();
  const showProfile = !id || team?.permissions?.edit;

  const showTags = !id
    ? !rootEventId
    : !!(team?.permissions?.edit && !team.root_event);
  const watchedIsManager = useWatch({ control, name: 'isManager' }) ?? false;
  const watchedTitle = useWatch({ control, name: 'title' }) || '';
  const watchedColor = useWatch({ control, name: 'color' }) || '';
  const watchedLocalAvatarUrl =
    useWatch({ control, name: 'localAvatarUrl' }) || null;
  const watchedTagGroupId = useWatch({ control, name: 'tagGroupId' }) || null;

  const headerTitle = () => (
    <Header.TitleContainer>
      <Box flex>
        <Text flexShrink={1} size="xl" numberOfLines={1}>
          {id ? 'Edit' : 'Create'} Team
        </Text>
        {rootEventTitle ? (
          <Box flex direction="horizontal" alignItems="center" space="sm">
            <Icon
              variant="typographySecondary"
              name="globe-outline"
              size="sm"
            />
            <Text variant="typographySecondary" size="xs">
              {rootEventTitle}
            </Text>
          </Box>
        ) : null}
      </Box>
    </Header.TitleContainer>
  );

  const actions = {
    manage: Boolean(team?.root_event?.permissions?.delegate),
    createInvite: !!team?.permissions?.create_invite,
    transfer:
      !team?.root_event &&
      !!(personCount > 1 && team?.permissions?.edit_others_role),
    delete: Boolean(
      team?.permissions?.delete || team?.root_event?.permissions?.edit,
    ),
    leave: !!team?.permissions?.delete_self && team.on_team,
  };
  const isCreateProfileValid =
    !!id ||
    (watchedTitle.trim().length > 0 &&
      (!showTags || Boolean(watchedTagGroupId)));
  const isSaveDisabled =
    isSubmitting || (id ? !isDirty : !isCreateProfileValid);

  return (
    <>
      <Stack.Screen
        options={{
          headerTitle,
          title: `${id ? 'Edit' : 'Create'} Team`,
        }}
      />
      <FormContentBoundary loading={isLoading}>
        <FormProvider {...form}>
          <ModalKeyboardAwareScrollView>
            <Box space="sm" p="sm">
              {showProfile || showTags ? (
                <TeamProfileFields
                  autoFocus={!id}
                  color={watchedColor}
                  localAvatarUrl={watchedLocalAvatarUrl}
                  showAvatarPreview={showProfile}
                  showTagGroup={showTags}
                  showTitle={showProfile}
                  tagGroupId={watchedTagGroupId}
                  title={watchedTitle}
                  onChangeColor={(nextColor) => {
                    setValue('color', nextColor, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  onChangeLocalAvatarUrl={(nextLocalAvatarUrl) => {
                    setValue('localAvatarUrl', nextLocalAvatarUrl, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  onChangeTagGroupId={(nextTagGroupId) => {
                    setValue('tagGroupId', nextTagGroupId, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  onChangeTitle={(nextTitle) => {
                    setValue('title', nextTitle, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                />
              ) : null}

              {errors.root?.message ? (
                <Text role="alert" variant="error">
                  {errors.root.message}
                </Text>
              ) : null}

              {id && team && Object.values(actions).some(Boolean) ? (
                <Card>
                  {actions.manage && team.root_event ? (
                    <Item
                      disabled={isSubmitting}
                      onPress={() =>
                        setValue('isManager', !watchedIsManager, {
                          shouldDirty: true,
                        })
                      }
                    >
                      <Box direction="horizontal" gap="lg" flex align="center">
                        <Icon name="people-outline" />
                        <Box flex space="xs">
                          <Text>Management Team</Text>
                          <Text size="sm" variant="typographySecondary">
                            Members can manage {team.root_event.title} and its
                            events based on their team roles.
                          </Text>
                        </Box>
                      </Box>
                      <Switch
                        accessibilityLabel="Management Team"
                        value={watchedIsManager}
                        disabled={isSubmitting}
                        onValueChange={(isManager) =>
                          setValue('isManager', isManager, {
                            shouldDirty: true,
                          })
                        }
                      />
                    </Item>
                  ) : null}
                  {actions.createInvite ? <TeamEditInvite /> : null}
                  {actions.transfer && (
                    <Link
                      href={{
                        pathname: `${pathname}/transfer`,
                        params: { teamId: id, title: team.title },
                      }}
                      asChild
                    >
                      <Item>
                        <Icon name="swap-horizontal-outline" />
                        <Text>Transfer Team…</Text>
                      </Item>
                    </Link>
                  )}
                  {actions.delete ? (
                    <Link
                      href={{
                        pathname: `./edit/delete`,
                        params: { title: team.title },
                      }}
                      asChild
                    >
                      <Item>
                        <Icon name="trash-outline" />
                        <Text>Delete Team…</Text>
                      </Item>
                    </Link>
                  ) : null}
                  {actions.leave ? (
                    <Link
                      href={{
                        pathname: '/team/[teamId]/edit/leave',
                        params: { teamId: team.id },
                      }}
                      asChild
                    >
                      <Item>
                        <Icon name="log-out-outline" />
                        <Text>Leave Team…</Text>
                      </Item>
                    </Link>
                  ) : null}
                </Card>
              ) : null}
            </Box>
          </ModalKeyboardAwareScrollView>
          <ModalKeyboardStickyView>
            <FormSaveButton
              info={{ text: 'Save' }}
              isDisabled={isSaveDisabled}
              onPress={handleSubmit(onSubmit)}
            />
          </ModalKeyboardStickyView>
        </FormProvider>
      </FormContentBoundary>
    </>
  );
}
